import React from 'react';
import { DesignSystem } from '../styles/designSystem';
import { RISK_LEVELS, normalizeRiskLevel, type RiskLevel } from '../constants/project';

interface TaskRisksWidgetProps {
  projects: any[];
  riskFilter?: string; // 'all', 'high', 'medium', 'low', 'critical', 'none'
}

interface RiskStats {
  [RISK_LEVELS.NONE]: number;
  [RISK_LEVELS.LOW]: number;
  [RISK_LEVELS.MEDIUM]: number;
  [RISK_LEVELS.HIGH]: number;
  [RISK_LEVELS.CRITICAL]: number;
}

const RiskColors = {
  [RISK_LEVELS.NONE]: DesignSystem.colors.semantic.risk.none,     // gray-500
  [RISK_LEVELS.LOW]: DesignSystem.colors.semantic.risk.low,       // green-500
  [RISK_LEVELS.MEDIUM]: DesignSystem.colors.semantic.risk.medium, // yellow-500
  [RISK_LEVELS.HIGH]: DesignSystem.colors.semantic.risk.high,     // red-500
  [RISK_LEVELS.CRITICAL]: DesignSystem.colors.semantic.risk.high, // red-500 (same as high)
};

const RiskLabels = {
  [RISK_LEVELS.NONE]: 'No Risk',
  [RISK_LEVELS.LOW]: 'Low Risk',
  [RISK_LEVELS.MEDIUM]: 'Medium Risk',
  [RISK_LEVELS.HIGH]: 'High Risk',
  [RISK_LEVELS.CRITICAL]: 'Critical Risk',
};

export const TaskRisksWidget: React.FC<TaskRisksWidgetProps> = ({ projects, riskFilter = 'all' }) => {
  // Normalize risk values using centralized utility
  const normalizeRisk = (risk: string | null | undefined): RiskLevel => {
    return normalizeRiskLevel(risk);
  };

  // Calculate task risk statistics from all projects
  const allTasks = projects.flatMap(project => project.items || []);
  
  // Filter tasks based on risk filter if not 'all'
  const filteredTasks = riskFilter === 'all' 
    ? allTasks 
    : allTasks.filter(task => {
        const taskRisk = normalizeRisk(task.risks);
        return taskRisk.toLowerCase() === riskFilter.toLowerCase();
      });
  
  // When filter is applied, show only tasks of that risk level
  // When no filter, show all tasks with their risk distribution
  const tasksForStats = riskFilter === 'all' ? allTasks : filteredTasks;

  const stats: RiskStats = tasksForStats.reduce((acc, task) => {
    const risk = normalizeRisk(task.risks);
    acc[risk] = acc[risk] + 1;
    return acc;
  }, { 
    [RISK_LEVELS.NONE]: 0, 
    [RISK_LEVELS.LOW]: 0, 
    [RISK_LEVELS.MEDIUM]: 0, 
    [RISK_LEVELS.HIGH]: 0, 
    [RISK_LEVELS.CRITICAL]: 0 
  });

  const total = tasksForStats.length;
  
  if (total === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-5 w-64">
        <h3 className="text-lg font-semibold text-gray-600 mb-4">Task Risks</h3>
        <div className="flex items-center justify-center h-24 text-gray-400">
          <span className="text-sm">No tasks available</span>
        </div>
      </div>
    );
  }

  // Calculate percentages and prepare data for display
  const riskData = Object.entries(stats).map(([risk, count]) => ({
    risk: risk as keyof RiskStats,
    count,
    percentage: total > 0 ? (count / total * 100) : 0,
    color: RiskColors[risk as keyof RiskStats],
    label: RiskLabels[risk as keyof RiskStats]
  }));

  // When filter is applied, show only that risk level
  // When no filter, show all risk levels (excluding NONE unless it has tasks)
  const riskLevelsToShow = riskFilter === 'all' 
    ? Object.values(RISK_LEVELS).filter(level => level !== RISK_LEVELS.NONE || stats[RISK_LEVELS.NONE] > 0)
    : [normalizeRiskLevel(riskFilter)];
    
  const orderedRiskData = riskLevelsToShow.map(riskLevel => 
    riskData.find(item => item.risk === riskLevel) || {
      risk: riskLevel,
      count: 0,
      percentage: 0,
      color: RiskColors[riskLevel],
      label: RiskLabels[riskLevel]
    }
  );

  // Find max count for scaling bars (avoid division by zero)
  const maxCount = Math.max(...riskData.map(item => item.count), 1);

  // Get title based on filter
  const getTitle = () => {
    if (riskFilter === 'all') return 'Task Risks';
    return `${riskFilter.charAt(0).toUpperCase() + riskFilter.slice(1)} Risk Tasks`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-5 w-64 relative" style={{ zIndex: 1 }}>
      <h3 className="text-lg font-semibold text-gray-600 mb-5">{getTitle()}</h3>
      
      <div className="space-y-3">
        {orderedRiskData.map((item) => (
          <div key={item.risk} className="space-y-1.5">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs font-medium text-gray-700">
                  {item.label}
                </span>
              </div>
              <div className="text-xs text-gray-600">
                {item.count} {item.count > 0 && `(${Math.round(item.percentage)}%)`}
              </div>
            </div>
            
            {/* Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all duration-500 ease-out"
                style={{ 
                  backgroundColor: item.color, 
                  width: item.count > 0 ? `${(item.count / maxCount) * 100}%` : '0%'
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="grid grid-cols-2 gap-3 text-center">
          <div>
            <div className="text-base font-semibold text-red-600">
              {stats.HIGH}
            </div>
            <div className="text-xs text-gray-500">High Risk</div>
          </div>
          <div>
            <div className="text-base font-semibold text-gray-700">
              {total}
            </div>
            <div className="text-xs text-gray-500">Total Tasks</div>
          </div>
        </div>
      </div>
    </div>
  );
}; 